import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000; // 60s — light poll so the banner appears / disappears without page reload

/**
 * Reads `/api/public/platform-status` (unauth) and renders a sticky top banner
 * when `maintenance_mode` is true. Hidden on admin routes so operators can
 * still turn the flag off.
 *
 * Warm amber (informational), NOT red. Terracotta palette only.
 */
export default function MaintenanceBanner() {
  const [on, setOn] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API}/api/public/platform-status`, { timeout: 8000 });
        if (!cancelled) setOn(Boolean(r?.data?.maintenance_mode));
      } catch {
        // Network hiccup — don't flip the banner state; keep last-known value
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!on || isAdmin) return null;

  return (
    <div
      data-testid="maintenance-banner"
      role="status"
      aria-live="polite"
      className="sticky top-0 z-[60] w-full border-b border-amber-300 bg-amber-100 text-amber-900"
    >
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-2 text-xs sm:text-sm">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="font-medium">
          AfriTask is under maintenance — we&apos;ll be back shortly.
        </span>
      </div>
    </div>
  );
}
